import chartCapture from './chartCapture';

// 有設計師排版 SVG 的縣市清單
const SVG_MAP = {
	'高雄市': '/MapSvg/kaohsiung.svg',
	'南投縣': '/MapSvg/nantou.svg',
	'臺南市': '/MapSvg/tainan.svg',
	'臺北市': '/MapSvg/taipei.svg',
	'臺中市': '/MapSvg/taichung.svg',
	'金門縣': '/MapSvg/kinmen.svg',
	'宜蘭縣': '/MapSvg/yilan.svg',
	'全臺': '/MapSvg/taiwan.svg',
};

/**
 * 檢查該縣市是否有設計師排版的 SVG
 */
export const hasSvgMap = (countyName) => {
	return !!SVG_MAP[countyName];
};

/**
 * 從 SviChartMap 的隱藏地圖讀取每個行政區的顏色
 */
function readColorsFromHiddenMap() {
	const colorMap = {};
	const hiddenMap = document.querySelector('.captureText-hidden');
	if (!hiddenMap) return colorMap;

	const paths = hiddenMap.querySelectorAll('path');
	paths.forEach(function (path) {
		const townEl = path.querySelector('town');
		const countyEl = path.querySelector('county');
		const nameEl = (townEl && townEl.textContent) ? townEl : (countyEl && countyEl.textContent) ? countyEl : null;
		if (nameEl) {
			const fill = path.style.fill || path.getAttribute('fill');
			if (fill) {
				colorMap[nameEl.textContent] = fill;
			}
		}
	});
	return colorMap;
}

/**
 * 使用設計師排版好的 SVG 地圖來下載含數值的 PNG
 */
const svgMapCapture = async (countyName, districtData, fileName, titleText, showValues) => {
	if (showValues === undefined) showValues = true;
	const svgUrl = SVG_MAP[countyName];
	if (!svgUrl) return;

	// 1. 載入 SVG
	const response = await fetch(svgUrl);
	if (!response.ok) return;
	const svgText = await response.text();

	// 2. 建立隱藏容器，放入 SVG（需要在 DOM 中才能使用 isPointInFill）
	const container = document.createElement('div');
	container.style.cssText = 'position:fixed; left:0; top:0; width:880px; z-index:-9999; opacity:0; pointer-events:none;';
	container.innerHTML = svgText;
	document.body.appendChild(container);

	const svgEl = container.querySelector('svg');
	if (!svgEl) {
		container.remove();
		return;
	}

	// 3. 從隱藏地圖讀取顏色
	const dataMap = {};
	districtData.forEach(function (item) {
		dataMap[item.name] = item;
	});
	const colorMap = readColorsFromHiddenMap();

	// 4. 從文字標籤提取行政區名稱、位置、數值文字元素
	var districtPositions = [];
	var chinesePattern = /[\u4e00-\u9fff]/;

	// 取得 text 或 tspan 元素的座標（支援 translate 和 matrix 兩種格式）
	function getTextPosition(el) {
		// 先從元素本身找 transform
		var node = el;
		while (node && node !== svgEl) {
			var tf = node.getAttribute('transform');
			if (tf) {
				// translate(x y)
				var tMatch = tf.match(/translate\(([^ ,]+)[\s,]+([^)]+)\)/);
				if (tMatch) return { x: parseFloat(tMatch[1]), y: parseFloat(tMatch[2]) };
				// matrix(a,b,c,d,e,f) → e=x, f=y
				var mMatch = tf.match(/matrix\([^,]+,[^,]+,[^,]+,[^,]+,([^,]+),([^)]+)\)/);
				if (mMatch) return { x: parseFloat(mMatch[1]), y: parseFloat(mMatch[2]) };
			}
			node = node.parentElement;
		}
		return null;
	}

	// 嘗試格式一：高雄市格式（g.st2 巢狀結構 + tspan）
	var textGroups = container.querySelectorAll('svg > g.st2');
	if (textGroups.length > 0) {
		textGroups.forEach(function (group) {
			var subGroups = group.children;
			if (subGroups.length < 2) return;

			var nameGroup = subGroups[0];
			var valueGroup = subGroups[1];

			var nameTexts = [];
			nameGroup.querySelectorAll('tspan').forEach(function (tspan) {
				nameTexts.push(tspan.textContent);
			});

			var valueTextEl = valueGroup.querySelector('tspan');
			var name = nameTexts.join('');
			if (!name) return;

			var firstText = group.querySelector('text');
			var pos = getTextPosition(firstText);
			if (!pos) return;

			districtPositions.push({
				name: name,
				x: pos.x,
				y: pos.y,
				nameTextEl: nameGroup,
				valueTextEl: valueTextEl,
			});
		});
	}

	// 嘗試格式二：南投縣格式（獨立 text 元素，用中文/數字區分名稱和數值）
	if (districtPositions.length === 0) {
		var allTexts = container.querySelectorAll('text');
		var nameItems = [];
		var valueItems = [];

		allTexts.forEach(function (textEl) {
			var content = textEl.textContent.trim();
			if (!content) return;
			var pos = getTextPosition(textEl);
			if (!pos) return;

			if (chinesePattern.test(content)) {
				nameItems.push({ name: content, x: pos.x, y: pos.y, textEl: textEl });
			} else {
				valueItems.push({ value: content, x: pos.x, y: pos.y, textEl: textEl });
			}
		});

		// 每個名稱配對最近的數值
		nameItems.forEach(function (nameItem) {
			var bestValue = null;
			var bestDist = Infinity;
			for (var v = 0; v < valueItems.length; v++) {
				if (valueItems[v].used) continue;
				var dx = valueItems[v].x - nameItem.x;
				var dy = valueItems[v].y - nameItem.y;
				var dist = dx * dx + dy * dy;
				if (dist < bestDist) {
					bestDist = dist;
					bestValue = valueItems[v];
				}
			}
			if (bestValue) {
				bestValue.used = true;
			}

			districtPositions.push({
				name: nameItem.name,
				x: nameItem.x,
				y: nameItem.y,
				nameTextEl: nameItem.textEl,
				valueTextEl: bestValue ? bestValue.textEl : null,
			});
		});
	}

	// 5. 建立拉線對應表
	var leaderLines = [];
	svgEl.querySelectorAll('polyline').forEach(function (pl) {
		var pts = pl.getAttribute('points');
		if (!pts) return;
		var coords = pts.trim().split(/[\s,]+/).map(Number);
		if (coords.length >= 4) {
			leaderLines.push({
				startX: coords[0],
				startY: coords[1],
				endX: coords[coords.length - 2],
				endY: coords[coords.length - 1],
				used: false,
			});
		}
	});
	svgEl.querySelectorAll('line').forEach(function (ln) {
		leaderLines.push({
			startX: parseFloat(ln.getAttribute('x1')),
			startY: parseFloat(ln.getAttribute('y1')),
			endX: parseFloat(ln.getAttribute('x2')),
			endY: parseFloat(ln.getAttribute('y2')),
			used: false,
		});
	});

	// 5b. 從牽引線圖層解析 path 格式的拉線（南投縣等格式）
	var leaderLayer = svgEl.querySelector('[id*="牽引線"]');
	console.log('牽引線圖層:', leaderLayer ? '找到' : '未找到');
	if (leaderLayer) {
		var leaderPaths = leaderLayer.querySelectorAll('path');
		console.log('牽引線 path 數量:', leaderPaths.length);
		leaderPaths.forEach(function (p) {
			var d = p.getAttribute('d');
			if (!d) return;
			// 解析起點（M x,y）
			var moveMatch = d.match(/M\s*([\d.]+)[,\s]+([\d.]+)/);
			if (!moveMatch) return;

			var sx = parseFloat(moveMatch[1]);
			var sy = parseFloat(moveMatch[2]);
			var ex, ey;

			// 逐步解析 path 指令，算出終點座標（支援大寫絕對座標和小寫相對座標）
			var curX = sx, curY = sy;
			var remaining = d.replace(/^M\s*[\d.]+[,\s]+[\d.]+/, '').trim();
			// 處理隱式 lineto（M 後直接跟座標對，沒有明確的 L 指令）
			var implicitMatch = remaining.match(/^[,\s]*([\d.]+)[,\s]+([\d.]+)/);
			if (implicitMatch) {
				curX = parseFloat(implicitMatch[1]);
				curY = parseFloat(implicitMatch[2]);
				remaining = remaining.substring(implicitMatch[0].length).trim();
			}
			var cmds = remaining.match(/[LlHhVv][^LlHhVv]*/g) || [];
			if (cmds.length === 0 && !implicitMatch) {
				return;
			}
			for (var ci = 0; ci < cmds.length; ci++) {
				var type = cmds[ci].trim()[0];
				var nums = cmds[ci].trim().substring(1).trim().match(/-?(?:\d+\.?\d*|\.\d+)/g).map(Number);
				if (type === 'L') { curX = nums[0]; curY = nums[1]; }
				else if (type === 'l') { curX += nums[0]; curY += nums[1]; }
				else if (type === 'H') { curX = nums[0]; }
				else if (type === 'h') { curX += nums[0]; }
				else if (type === 'V') { curY = nums[0]; }
				else if (type === 'v') { curY += nums[0]; }
			}
			ex = curX;
			ey = curY;

			// 處理 translate 或 matrix 座標轉換（元素本身和父元素）
			var nodes = [p, p.parentElement];
			for (var ti = 0; ti < nodes.length; ti++) {
				if (!nodes[ti]) continue;
				var tf = nodes[ti].getAttribute('transform');
				if (!tf) continue;
				var tMatch = tf.match(/translate\(([^ ,]+)[\s,]+([^)]+)\)/);
				if (tMatch) {
					sx += parseFloat(tMatch[1]); sy += parseFloat(tMatch[2]);
					ex += parseFloat(tMatch[1]); ey += parseFloat(tMatch[2]);
				}
				var pm = tf.match(/matrix\(([^,]+),([^,]+),([^,]+),([^,]+),([^,]+),([^)]+)\)/);
				if (pm) {
					var a = parseFloat(pm[1]), b = parseFloat(pm[2]);
					var c = parseFloat(pm[3]), dd = parseFloat(pm[4]);
					var e = parseFloat(pm[5]), f = parseFloat(pm[6]);
					var sx2 = a * sx + c * sy + e;
					var sy2 = b * sx + dd * sy + f;
					var ex2 = a * ex + c * ey + e;
					var ey2 = b * ex + dd * ey + f;
					sx = sx2; sy = sy2;
					ex = ex2; ey = ey2;
				}
			}

			console.log('拉線 path:', '起點:', sx.toFixed(1), sy.toFixed(1), '終點:', ex.toFixed(1), ey.toFixed(1));
			leaderLines.push({
				startX: sx, startY: sy,
				endX: ex, endY: ey,
				used: false,
			});
		});
	}
	console.log('拉線總數:', leaderLines.length);

	// 6. 取得行政區的 path 元素（優先從行政區/Dissolved 圖層找，避免抓到背景 path）
	var districtLayers = svgEl.querySelectorAll('[id*="行政區"]:not([id*="Labels"]), [id*="Dissolved"]:not([id*="Labels"])');
	var paths = [];
	if (districtLayers.length > 0) {
		districtLayers.forEach(function (layer) {
			layer.querySelectorAll('path').forEach(function (p) { paths.push(p); });
		});
	} else {
		paths = svgEl.querySelectorAll('path');
	}

	// 測試一個點是否在 path 的填充區域內（自動處理座標轉換）
	function testPointInPath(path, x, y) {
		try {
			var point = svgEl.createSVGPoint();
			// 將 SVG 根座標系的點轉換到 path 的本地座標系
			var svgCTM = svgEl.getScreenCTM();
			var pathCTM = path.getScreenCTM();
			if (svgCTM && pathCTM) {
				point.x = x;
				point.y = y;
				var screenPoint = point.matrixTransform(svgCTM);
				var localPoint = screenPoint.matrixTransform(pathCTM.inverse());
				point.x = localPoint.x;
				point.y = localPoint.y;
			} else {
				point.x = x;
				point.y = y;
			}
			return path.isPointInFill(point);
		} catch (e) {
			return false;
		}
	}

	// 7. 配對並上色
	// 偏移量分兩階段：前 12 個為小範圍（±40px 以內），後面為大範圍。
	// 如果小範圍全部沒命中，代表文字在地圖外面，直接交給拉線配對，避免大偏移搶到鄰近行政區的 path。
	var smallOffsetCount = 12;
	districtPositions.forEach(function (district) {
		var data = dataMap[district.name];

		if (!showValues) {
			// 不顯示文字：清空名稱和數值
			if (district.nameTextEl) district.nameTextEl.textContent = '';
			if (district.valueTextEl) district.valueTextEl.textContent = '';
		} else if (district.valueTextEl) {
			if (data && data.value !== null && data.value !== undefined) {
				district.valueTextEl.textContent = data.value;
			} else {
				district.valueTextEl.textContent = '查無資料';
			}
		}

		if (!data) return;

		var matchedPath = null;
		var textOffsets = [
			[10, -5], [30, 5], [10, 10], [40, -10], [0, 0], [20, 15],
			[0, 20], [10, 25], [0, 30], [20, -15], [-10, 10], [5, 35],
			[-20, 0], [0, -20], [50, 0], [0, 50], [-30, -10], [60, 10],
			[-10, -30], [30, -30], [-40, 20], [70, -20], [0, -40], [-50, 0],
			[80, 0], [0, -60], [-60, -20], [90, 10], [-70, 0], [0, 70],
			[100, -10], [-80, 10], [50, -50], [-50, -40], [0, -80], [100, 0]
		];

		for (var t = 0; t < textOffsets.length; t++) {
			// 小範圍偏移全部沒命中且有拉線可用時，直接交給拉線配對
			if (t === smallOffsetCount && leaderLines.length > 0) {
				district._needLeaderLine = true;
				console.log('小範圍未命中，交給拉線配對:', district.name);
				return;
			}
			var tx = district.x + textOffsets[t][0];
			var ty = district.y + textOffsets[t][1];
			for (var i = 0; i < paths.length; i++) {
				if (testPointInPath(paths[i], tx, ty) && !paths[i].dataset.matched) {
					matchedPath = paths[i];
					break;
				}
			}
			if (matchedPath) break;
		}

		if (!matchedPath) {
			district._needLeaderLine = true;
		} else {
			var color = colorMap[district.name];
			console.log('配對:', district.name, '顏色:', color);
			if (color) {
				matchedPath.style.fill = color;
				matchedPath.style.fillOpacity = '1';
			}
			matchedPath.style.stroke = '#555555';
			matchedPath.dataset.matched = district.name;
		}
	});

	// 7-2. 需要拉線的行政區：全域最佳配對（避免處理順序造成搶錯拉線）
	var needLeader = districtPositions.filter(function (d) { return d._needLeaderLine && dataMap[d.name]; });
	while (needLeader.length > 0) {
		var globalBestDist = Infinity;
		var globalBestIdx = -1;
		var globalBestLine = null;
		var globalBestReversed = false;
		for (var n = 0; n < needLeader.length; n++) {
			for (var l = 0; l < leaderLines.length; l++) {
				if (leaderLines[l].used) continue;
				var dxE = leaderLines[l].endX - needLeader[n].x;
				var dyE = leaderLines[l].endY - needLeader[n].y;
				var distE = dxE * dxE + dyE * dyE;
				var dxS = leaderLines[l].startX - needLeader[n].x;
				var dyS = leaderLines[l].startY - needLeader[n].y;
				var distS = dxS * dxS + dyS * dyS;
				if (distE < globalBestDist) {
					globalBestDist = distE;
					globalBestIdx = n;
					globalBestLine = leaderLines[l];
					globalBestReversed = false;
				}
				if (distS < globalBestDist) {
					globalBestDist = distS;
					globalBestIdx = n;
					globalBestLine = leaderLines[l];
					globalBestReversed = true;
				}
			}
		}
		if (!globalBestLine || globalBestDist >= 40000) break;

		var dist = needLeader[globalBestIdx];
		var mapX = globalBestReversed ? globalBestLine.endX : globalBestLine.startX;
		var mapY = globalBestReversed ? globalBestLine.endY : globalBestLine.startY;
		var textSideX = globalBestReversed ? globalBestLine.startX : globalBestLine.endX;
		var textSideY = globalBestReversed ? globalBestLine.startY : globalBestLine.endY;
		console.log('拉線配對:', dist.name, '反轉:', globalBestReversed, '測試點:', mapX.toFixed(1), mapY.toFixed(1), '距離²:', Math.round(globalBestDist));

		// 沿拉線方向及周圍多點取樣，增加命中小區塊的機率
		var dx = textSideX - mapX;
		var dy = textSideY - mapY;
		var samplePoints = [
			[mapX, mapY],
			[mapX - dx * 0.05, mapY - dy * 0.05],
			[mapX - dx * 0.1, mapY - dy * 0.1],
			[mapX + 5, mapY], [mapX - 5, mapY],
			[mapX, mapY + 5], [mapX, mapY - 5],
			[mapX + 5, mapY + 5], [mapX - 5, mapY - 5],
			[mapX - dx * 0.15, mapY - dy * 0.15],
		];
		var matchedPath = null;
		for (var s = 0; s < samplePoints.length; s++) {
			var testX = samplePoints[s][0];
			var testY = samplePoints[s][1];
			for (var j = 0; j < paths.length; j++) {
				if (testPointInPath(paths[j], testX, testY) && !paths[j].dataset.matched) {
					matchedPath = paths[j];
					globalBestLine.used = true;
					console.log('  → 配對到 path#' + j, '取樣點#' + s);
					break;
				}
			}
			if (matchedPath) break;
		}
		if (matchedPath) {
			var color = colorMap[dist.name];
			console.log('拉線配對成功:', dist.name, '顏色:', color);
			if (color) {
				matchedPath.style.fill = color;
				matchedPath.style.fillOpacity = '1';
			}
			matchedPath.style.stroke = '#555555';
			matchedPath.dataset.matched = dist.name;
		} else {
			console.log('  → 未配對到任何 path');
		}
		needLeader.splice(globalBestIdx, 1);
	}
	needLeader.forEach(function (d) {
		console.log('未配對:', d.name, '位置:', d.x.toFixed(1), d.y.toFixed(1));
	});

	// 7b. 未配對 path 上色（處理多島嶼行政區）
	// 方法一：同一個父群組只有一個行政區時，直接跟同色
	for (var u = 0; u < paths.length; u++) {
		if (paths[u].dataset.matched) continue;
		var parent = paths[u].parentElement;
		if (!parent) continue;
		var siblings = parent.querySelectorAll('path[data-matched]');
		if (siblings.length === 0) continue;
		var districtNames = {};
		siblings.forEach(function (sib) { districtNames[sib.dataset.matched] = true; });
		if (Object.keys(districtNames).length !== 1) continue;
		var onlyName = Object.keys(districtNames)[0];
		var onlySib = siblings[0];
		paths[u].style.fill = onlySib.style.fill;
		paths[u].style.fillOpacity = '1';
		paths[u].style.stroke = '#555555';
		paths[u].dataset.matched = onlyName;
	}

	// 8. 不含數值時隱藏拉線和文字；含數值時移到最上層避免被蓋住
	var leaderLayerForOrder = svgEl.querySelector('[id*="牽引線"]');
	var labelsLayer = svgEl.querySelector('[id*="Labels"]');

	if (!showValues) {
		// 隱藏拉線和文字
		if (leaderLayerForOrder) leaderLayerForOrder.style.display = 'none';
		if (labelsLayer) labelsLayer.style.display = 'none';
		// 隱藏不在牽引線圖層內的拉線元素（如圓點）
		svgEl.querySelectorAll('polyline, line, circle').forEach(function (el) {
			el.style.display = 'none';
		});
		container.querySelectorAll('text').forEach(function (el) {
			el.style.display = 'none';
		});
	} else {
		// 含數值：移到最上層
		if (leaderLayerForOrder) svgEl.appendChild(leaderLayerForOrder);
		if (labelsLayer) svgEl.appendChild(labelsLayer);
		if (!leaderLayerForOrder) {
			svgEl.querySelectorAll('polyline, line, circle').forEach(function (el) {
				el.parentNode.appendChild(el);
			});
		}
		container.querySelectorAll('text').forEach(function (el) {
			el.parentNode.appendChild(el);
		});
	}

	// 9. 將修改好的 SVG 轉成 base64 圖片
	var svgWidth = 880;
	var viewBox = svgEl.getAttribute('viewBox');
	var vbParts = viewBox ? viewBox.split(/\s+/) : [0, 0, 1303, 1430];
	var vbW = parseFloat(vbParts[2]);
	var vbH = parseFloat(vbParts[3]);
	var svgHeight = svgWidth * (vbH / vbW);

	svgEl.setAttribute('width', svgWidth);
	svgEl.setAttribute('height', svgHeight);
	svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

	var serializer = new XMLSerializer();
	var svgString = serializer.serializeToString(svgEl);
	var svgBase64 = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));

	container.remove();

	// 9. 把 SVG 圖片插入隱藏地圖容器，取代原本的地圖
	var hiddenEl = document.querySelector('.captureText-hidden');
	if (!hiddenEl) return;

	var taiwanMap = hiddenEl.querySelector('.taiwan-map');
	if (!taiwanMap) return;

	// 隱藏原本的地圖
	var originalDisplay = taiwanMap.style.display;
	taiwanMap.style.display = 'none';

	// 插入 SVG 圖片
	var svgImg = document.createElement('img');
	svgImg.src = svgBase64;
	svgImg.style.cssText = 'width:100%; height:auto;';
	taiwanMap.parentNode.insertBefore(svgImg, taiwanMap);

	// 等圖片載入完成
	await new Promise(function (resolve) {
		if (svgImg.complete) {
			resolve();
		} else {
			svgImg.onload = resolve;
		}
	});

	// 10. 隱藏 captureText-hidden 裡的說明文字，改用頁面上可見的 .info.captureText 截圖
	var hiddenInfo = hiddenEl.querySelector('.info');
	if (hiddenInfo) hiddenInfo.style.display = 'none';

	// 11. 用原本的 chartCapture 截圖
	var captureArea = document.querySelectorAll('.heading.captureText, .captureText-hidden, .info.captureText');
	chartCapture(captureArea, fileName, undefined, function () {
		// 截圖完成後還原：移除 SVG 圖片，恢復原本地圖
		svgImg.remove();
		taiwanMap.style.display = originalDisplay;
		if (hiddenInfo) hiddenInfo.style.display = '';
	});
};

export default svgMapCapture;
